import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { getListingStatusSuffix } from '../../../utils/clickstreamUtils';

import { TRIGGER_LOCATIONS_MAP } from '../../../constants/ClickstreamConstants';

import SingleFamilyCostsAndFeesModal from '../../modals/SingleFamilyCostsAndFeesModal';
import { FeesByCategory } from './components/FeesByCategory';

import { emitClickAddSFFeeEvent, emitClickEditSFFeeEvent } from '../../../analytics/events/singleFamilyCostsAndFees';
import { useAuth } from '../../AuthProvider';

import type { SingleFamilyFeeDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  displayedLocation: string;
};

const CostsAndFeesFormField = ({ displayedLocation }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [showCostsAndFeesModal, setShowCostsAndFeesModal] = useState(false);
  const [feeToEdit, setFeeToEdit] = useState<SingleFamilyFeeDto | null>(null);
  const [fixedCategory, setFixedCategory] = useState('');

  const { data } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get(
    { listingAlias },
    {
      enabled: !!listingAlias,
      meta: {
        tags: ['sf-costsAndFees'],
        context: {
          api: 'useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get',
          details: 'CostsAndFeesFormField',
        },
      },
    },
  );

  const triggerLocation = useMemo(() => {
    const mappedLocation = TRIGGER_LOCATIONS_MAP[displayedLocation as keyof typeof TRIGGER_LOCATIONS_MAP];
    const listingStatusSuffix = getListingStatusSuffix(
      listingDetails?.active ?? false,
      listingDetails?.lastActivatedMs?.toString() ?? '',
    );
    return `${mappedLocation}${listingStatusSuffix}`;
  }, [listingDetails?.active, listingDetails?.lastActivatedMs, displayedLocation]);

  if (!data) {
    return null;
  }

  const handleAddFeeClick = (category = '') => {
    setShowCostsAndFeesModal(true);
    setFixedCategory(category);
    if (listingDetails && user) {
      emitClickAddSFFeeEvent({ currentProperty: listingDetails, user, triggerLocation });
    }
  };

  const handleEditFeeClick = (fee: SingleFamilyFeeDto) => {
    setFeeToEdit(fee);
    setFixedCategory(fee.feeCategory);
    setShowCostsAndFeesModal(true);
    if (listingDetails && user) {
      emitClickEditSFFeeEvent({ currentProperty: listingDetails, user, triggerLocation });
    }
  };

  const handleCloseFeeModal = () => {
    setShowCostsAndFeesModal(false);
    setFeeToEdit(null);
    setFixedCategory('');
  };

  return (
    <div data-testid="CostsAndFeesFormField">
      <FeesByCategory
        existingFees={data.existingFees}
        dictionaryItems={data.dictionaryItems}
        handleAddFeeClick={handleAddFeeClick}
        handleEditFeeClick={handleEditFeeClick}
        displayedLocation={displayedLocation}
      />

      {showCostsAndFeesModal && (
        <SingleFamilyCostsAndFeesModal
          onClose={handleCloseFeeModal}
          feeToEdit={feeToEdit}
          fixedCategory={feeToEdit?.feeCategory || fixedCategory}
        />
      )}
    </div>
  );
};

export default CostsAndFeesFormField;
